import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Bell, BellOff, Download, Inbox, Link2Off, Loader2, MoreVertical, Printer, RefreshCw, Send, Sheet, Trash2,
} from 'lucide-react';
import { api } from '../../../lib/api';
import { urlEnDocs } from '../../../lib/dominios';
import { varsTema } from '../../../lib/formularios';
import { useStore } from '../../../store/useStore';
import { Switch } from '../../ui/switch';
import { Button } from '../../ui/button';
import {
  DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import Resumen from './Resumen';
import VistaPregunta from './VistaPregunta';
import VistaIndividual from './VistaIndividual';
import AnalisisAurora from './AnalisisAurora';
import { AuroraLogo } from '../../office/icons';
import Impresion from './Impresion';
import { cargarTodas, guardarBlob, nombreDeDisposicion } from './datos';
import { mensajeDe } from '../responder/utilidades';
import '../forms.css';
import './respuestas.css';

/*
 * Pestaña "Respuestas" del editor de Orvian Forms.
 *
 *   <PestanaRespuestas formId formulario puedeEditar avisos onAvisos onPublicacion onRecargar />
 *
 * El formulario (y sus cambios: aceptar respuestas, avisos, hoja vinculada) lo guarda el editor;
 * aquí se llama a sus funciones para que su estado no se quede atrás. Todo lo demás (resumen,
 * respuestas, exportar, calificar) se pide directo a la API.
 */

const SUBPESTANAS = [
  { id: 'resumen', nombre: 'Resumen' },
  { id: 'pregunta', nombre: 'Pregunta' },
  { id: 'individual', nombre: 'Individual' },
  { id: 'aurora', nombre: 'Aurora IA', aurora: true }, // análisis con IA: solo quien edita (la pestaña ya lo es)
];

export default function PestanaRespuestas({ formId, formulario, puedeEditar, avisos, onAvisos, onPublicacion, onRecargar }) {
  const oscuro = useStore((s) => s.theme) === 'dark';
  // Las gráficas y tarjetas usan el tema del formulario (como el lienzo de Preguntas)
  const vars = useMemo(() => varsTema(formulario?.tema, { oscuro }), [formulario?.tema, oscuro]);
  const [sub, setSub] = useState('resumen');
  const [version, setVersion] = useState(0);
  const [resumen, setResumen] = useState({ cargando: true, datos: null, error: null });
  const [todas, setTodas] = useState({ cargando: false, items: null, error: null, version: -1 });
  const [indice, setIndice] = useState(0);
  const [imprimir, setImprimir] = useState(null);
  const [ocupado, setOcupado] = useState(null); // 'hoja' | 'csv' | 'xlsx' | 'imprimir' | 'publicar' | 'borrar'
  const [confirmarBorrar, setConfirmarBorrar] = useState(false);
  const publicacion = formulario?.publicacion || {};
  const [mensajeCerrado, setMensajeCerrado] = useState(publicacion.mensaje_cerrado || '');
  const totalFormulario = formulario?.respuestas_total ?? 0;

  useEffect(() => { setMensajeCerrado(publicacion.mensaje_cerrado || ''); }, [publicacion.mensaje_cerrado]);

  /* ---------- Resumen: al abrir, al llegar respuestas nuevas y al borrar ---------- */
  useEffect(() => {
    let vivo = true;
    setResumen((r) => ({ ...r, cargando: true, error: null }));
    api.get(`/forms/${formId}/resumen`)
      .then(({ data }) => vivo && setResumen({ cargando: false, datos: data, error: null }))
      .catch((err) => vivo && setResumen({ cargando: false, datos: null, error: mensajeDe(err, 'No se pudo cargar el resumen') }));
    return () => { vivo = false; };
  }, [formId, totalFormulario, version]);

  const total = resumen.datos?.total ?? totalFormulario;

  /* ---------- Todas las respuestas (solo la vista "Pregunta" las necesita) ---------- */
  useEffect(() => {
    if (sub !== 'pregunta') return undefined;
    const clave = `${version}:${totalFormulario}`;
    if (todas.version === clave) return undefined;
    let vivo = true;
    setTodas((t) => ({ ...t, cargando: true, error: null }));
    cargarTodas(formId)
      .then(({ items }) => vivo && setTodas({ cargando: false, items, error: null, version: clave }))
      .catch((err) => vivo && setTodas({ cargando: false, items: null, error: mensajeDe(err, 'No se pudieron cargar las respuestas'), version: clave }));
    return () => { vivo = false; };
    // `todas` se lee para no repetir la carga; no debe dispararla
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sub, formId, version, totalFormulario]);

  // Si se borran respuestas, el índice de la vista individual no puede quedar fuera de rango
  useEffect(() => {
    if (total > 0 && indice > total - 1) setIndice(total - 1);
  }, [total, indice]);

  const recargarTodo = useCallback(() => {
    setVersion((v) => v + 1);
    onRecargar?.();
  }, [onRecargar]);

  /* ---------- Acciones ---------- */
  const abrirHoja = (fileId) => window.open(urlEnDocs(`/spreadsheet/${fileId}`), '_blank', 'noopener');

  const vincularHoja = async () => {
    setOcupado('hoja');
    try {
      const { data } = await api.post(`/forms/${formId}/hoja`, {});
      const hoja = data?.hoja;
      onRecargar?.();
      // Abrir una pestaña después de esperar al servidor lo bloquea el navegador: se ofrece con un clic
      toast.success('Hoja de cálculo vinculada', hoja?.file_id ? {
        action: { label: 'Abrir', onClick: () => abrirHoja(hoja.file_id) },
      } : undefined);
    } catch (err) {
      toast.error(mensajeDe(err, 'No se pudo crear la hoja de cálculo'));
    } finally {
      setOcupado(null);
    }
  };

  const desvincularHoja = async () => {
    setOcupado('hoja');
    try {
      await api.delete(`/forms/${formId}/hoja`);
      toast.success('La hoja ya no se actualiza con las respuestas nuevas');
      onRecargar?.();
    } catch (err) {
      toast.error(mensajeDe(err, 'No se pudo desvincular la hoja'));
    } finally {
      setOcupado(null);
    }
  };

  const descargar = async (formato) => {
    setOcupado(formato);
    try {
      const res = await api.get(`/forms/${formId}/exportar`, { params: { formato }, responseType: 'blob' });
      const nombre = nombreDeDisposicion(res.headers?.['content-disposition'])
        || `${formulario?.titulo || 'Formulario'} (respuestas).${formato}`;
      guardarBlob(res.data, nombre);
    } catch (err) {
      toast.error(mensajeDe(err, 'No se pudieron descargar las respuestas'));
    } finally {
      setOcupado(null);
    }
  };

  const imprimirTodas = async () => {
    setOcupado('imprimir');
    try {
      const { items } = await cargarTodas(formId);
      if (!items.length) toast.message('No hay respuestas que imprimir');
      else setImprimir(items);
    } catch (err) {
      toast.error(mensajeDe(err, 'No se pudieron cargar las respuestas'));
    } finally {
      setOcupado(null);
    }
  };

  const publicarCalificaciones = async () => {
    setOcupado('publicar');
    try {
      const { data } = await api.post(`/forms/${formId}/calificaciones/publicar`, { ids: null });
      const n = data?.publicadas ?? 0;
      const correos = data?.correos_enviados ?? 0;
      toast.success(`${n} ${n === 1 ? 'calificación publicada' : 'calificaciones publicadas'}${correos ? ` · ${correos} por correo` : ''}`);
      setVersion((v) => v + 1);
    } catch (err) {
      toast.error(mensajeDe(err, 'No se pudieron publicar las calificaciones'));
    } finally {
      setOcupado(null);
    }
  };

  const borrarTodas = async () => {
    setConfirmarBorrar(false);
    setOcupado('borrar');
    try {
      const { data } = await api.delete(`/forms/${formId}/respuestas`);
      toast.success(`${data?.borradas ?? 'Todas las'} respuestas eliminadas`);
      setIndice(0);
      recargarTodo();
    } catch (err) {
      toast.error(mensajeDe(err, 'No se pudieron eliminar las respuestas'));
    } finally {
      setOcupado(null);
    }
  };

  const cambiarAcepta = async (acepta) => {
    try {
      await onPublicacion?.({ acepta_respuestas: acepta });
    } catch (err) {
      toast.error(mensajeDe(err, 'No se pudo cambiar'));
    }
  };

  const guardarMensajeCerrado = async () => {
    if ((publicacion.mensaje_cerrado || '') === mensajeCerrado) return;
    try {
      await onPublicacion?.({ mensaje_cerrado: mensajeCerrado });
    } catch (err) {
      toast.error(mensajeDe(err, 'No se pudo guardar el mensaje'));
    }
  };

  const cambiarAvisos = async (activos) => {
    try {
      await onAvisos?.(activos);
      toast.success(activos ? 'Recibirás un aviso con cada respuesta nueva' : 'Ya no recibirás avisos de respuestas nuevas');
    } catch (err) {
      toast.error(mensajeDe(err, 'No se pudo cambiar el aviso'));
    }
  };

  const acepta = publicacion.acepta_respuestas !== false;
  const hoja = formulario?.hoja?.file_id ? formulario.hoja : null;
  const esCuestionario = !!formulario?.ajustes?.es_cuestionario;

  return (
    <div className="fm-raiz rs-pestana" style={vars}>
      <div className="rs-columna">
        {/* ---------- Encabezado ---------- */}
        <section className="rs-tarjeta rs-cabecera" aria-label="Respuestas">
          <div className="rs-cabecera-fila">
            <h2 className="rs-total">
              {total} {total === 1 ? 'respuesta' : 'respuestas'}
            </h2>
            <div className="rs-cabecera-acciones">
              <button
                type="button"
                className="od-tool-btn"
                onClick={recargarTodo}
                aria-label="Actualizar respuestas"
                title="Actualizar"
              >
                <RefreshCw className={`h-[18px] w-[18px] ${resumen.cargando ? 'od-spin' : ''}`} />
              </button>
              {hoja ? (
                <button type="button" className="rs-boton-hoja" onClick={() => abrirHoja(hoja.file_id)}>
                  <Sheet className="h-4 w-4" /> <span>Ver en Hojas de cálculo</span>
                </button>
              ) : puedeEditar ? (
                <button type="button" className="rs-boton-hoja" onClick={vincularHoja} disabled={ocupado === 'hoja'}>
                  {ocupado === 'hoja' ? <Loader2 className="h-4 w-4 od-spin" /> : <Sheet className="h-4 w-4" />}
                  <span>Vincular con Hojas de cálculo</span>
                </button>
              ) : null}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button type="button" className="od-tool-btn" aria-label="Más opciones de respuestas">
                    <MoreVertical className="h-[18px] w-[18px]" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-72">
                  <DropdownMenuCheckboxItem checked={!!avisos} onCheckedChange={(v) => cambiarAvisos(!!v)}>
                    {avisos ? <Bell className="h-4 w-4 mr-2" /> : <BellOff className="h-4 w-4 mr-2" />}
                    Recibir avisos de respuestas nuevas
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => descargar('csv')} disabled={!total || !!ocupado}>
                    <Download className="h-4 w-4 mr-2" /> Descargar respuestas (.csv)
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => descargar('xlsx')} disabled={!total || !!ocupado}>
                    <Download className="h-4 w-4 mr-2" /> Descargar respuestas (.xlsx)
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={imprimirTodas} disabled={!total || !!ocupado}>
                    <Printer className="h-4 w-4 mr-2" /> Imprimir todas las respuestas
                  </DropdownMenuItem>
                  {puedeEditar && esCuestionario && (
                    <DropdownMenuItem onSelect={publicarCalificaciones} disabled={!total || !!ocupado}>
                      <Send className="h-4 w-4 mr-2" /> Publicar calificaciones
                    </DropdownMenuItem>
                  )}
                  {puedeEditar && hoja && (
                    <DropdownMenuItem onSelect={desvincularHoja} disabled={!!ocupado}>
                      <Link2Off className="h-4 w-4 mr-2" /> Desvincular hoja de cálculo
                    </DropdownMenuItem>
                  )}
                  {puedeEditar && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onSelect={() => setConfirmarBorrar(true)}
                        disabled={!total || !!ocupado}
                        className="rs-menu-peligro"
                      >
                        <Trash2 className="h-4 w-4 mr-2" /> Eliminar todas las respuestas
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {!publicacion.publicado && (
            <p className="rs-aviso">Publica el formulario para empezar a recibir respuestas.</p>
          )}

          <div className="rs-aceptando">
            <label htmlFor="rs-acepta" className={`rs-aceptando-texto ${acepta ? '' : 'is-cerrado'}`}>
              {acepta ? 'Aceptando respuestas' : 'No se aceptan respuestas'}
            </label>
            <Switch id="rs-acepta" checked={acepta} onCheckedChange={cambiarAcepta} disabled={!puedeEditar} />
          </div>
          {!acepta && (
            <div className="rs-mensaje-cerrado">
              <label htmlFor="rs-mensaje-cerrado" className="rs-subtitulo">Mensaje para quienes intenten responder</label>
              <textarea
                id="rs-mensaje-cerrado"
                rows={2}
                className="rs-textarea"
                placeholder="Este formulario ya no acepta respuestas"
                value={mensajeCerrado}
                disabled={!puedeEditar}
                onChange={(e) => setMensajeCerrado(e.target.value)}
                onBlur={guardarMensajeCerrado}
              />
            </div>
          )}
        </section>

        {/* ---------- Sin respuestas ---------- */}
        {total === 0 && !resumen.cargando && (
          <section className="rs-tarjeta rs-vacia">
            <Inbox className="h-10 w-10" aria-hidden="true" />
            <h3>Esperando respuestas</h3>
            <p>
              {publicacion.publicado
                ? 'Comparte el enlace del formulario: aquí verás las respuestas en cuanto lleguen.'
                : 'Cuando publiques el formulario y alguien responda, verás aquí el resumen y cada respuesta.'}
            </p>
          </section>
        )}

        {total > 0 && (
          <>
            <div className="rs-subpestanas" role="tablist" aria-label="Vista de respuestas">
              {SUBPESTANAS.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  role="tab"
                  id={`rs-tab-${s.id}`}
                  aria-selected={sub === s.id}
                  aria-controls={`rs-panel-${s.id}`}
                  className={`rs-subpestana ${sub === s.id ? 'is-activa' : ''} ${s.aurora ? 'is-aurora' : ''}`}
                  onClick={() => setSub(s.id)}
                >
                  {s.aurora && <AuroraLogo size={16} />}
                  {s.nombre}
                </button>
              ))}
            </div>

            <div role="tabpanel" id={`rs-panel-${sub}`} aria-labelledby={`rs-tab-${sub}`}>
              {sub === 'resumen' && (
                <>
                  {resumen.cargando && !resumen.datos && <div className="rs-cargando"><Loader2 className="h-7 w-7 od-spin" aria-label="Cargando resumen" /></div>}
                  {resumen.error && <p className="rs-vacio">{resumen.error}</p>}
                  {resumen.datos && <Resumen formulario={formulario} resumen={resumen.datos} />}
                </>
              )}
              {sub === 'pregunta' && (
                <>
                  {todas.cargando && <div className="rs-cargando"><Loader2 className="h-7 w-7 od-spin" aria-label="Cargando respuestas" /></div>}
                  {todas.error && <p className="rs-vacio">{todas.error}</p>}
                  {todas.items && !todas.cargando && (
                    <VistaPregunta
                      formulario={formulario}
                      respuestas={todas.items}
                      onVerRespuesta={(i) => {
                        setIndice(i);
                        setSub('individual');
                      }}
                    />
                  )}
                </>
              )}
              {sub === 'aurora' && <AnalisisAurora formId={formId} total={total} />}
              {sub === 'individual' && (
                <VistaIndividual
                  key={`${indice}-${version}`}
                  formId={formId}
                  formulario={formulario}
                  puedeEditar={puedeEditar}
                  total={total}
                  indice={indice}
                  onIndice={setIndice}
                  onBorrada={recargarTodo}
                  onImprimir={setImprimir}
                />
              )}
            </div>
          </>
        )}
      </div>

      <Impresion formulario={formulario} respuestas={imprimir} onTerminar={() => setImprimir(null)} />

      <Dialog open={confirmarBorrar} onOpenChange={setConfirmarBorrar}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>¿Eliminar todas las respuestas?</DialogTitle>
            <DialogDescription>
              Se borrarán las {total} respuestas de este formulario para siempre. La hoja de cálculo vinculada y los
              archivos subidos a Drive no se tocan.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmarBorrar(false)}>Cancelar</Button>
            <Button variant="destructive" onClick={borrarTodas} disabled={ocupado === 'borrar'}>Eliminar todas</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
